
import React from 'react';
import { Card } from "@/components/ui/card";
import { Target } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, LabelList } from 'recharts';
import type { PerformanceData } from './types';
import { useIsMobile } from '@/hooks/use-mobile';
import ResponsiveChartContainer from './ResponsiveChartContainer';
import ResponsiveChartTooltip from './ResponsiveChartTooltip';

interface StrengthWeaknessAnalysisProps {
  performanceData: PerformanceData[];
}

export const StrengthWeaknessAnalysis: React.FC<StrengthWeaknessAnalysisProps> = ({ performanceData }) => {
  const { isMobile, screenSize } = useIsMobile();

  const subjectPerformance = React.useMemo(() => {
    const subjectScores = performanceData.reduce((acc, curr) => {
      if (!curr.subject) return acc;
      if (!acc[curr.subject]) {
        acc[curr.subject] = { total: 0, count: 0 };
      }
      acc[curr.subject].total += curr.score;
      acc[curr.subject].count += 1;
      return acc;
    }, {} as Record<string, { total: number; count: number }>);

    return Object.entries(subjectScores)
      .map(([subject, data]) => ({
        subject,
        averageScore: data.total / data.count,
        // Categorize as strength or weakness
        category: data.total / data.count >= 75 ? 'strength' : 'weakness'
      }))
      .sort((a, b) => b.averageScore - a.averageScore);
  }, [performanceData]);

  // For mobile, limit the number of subjects shown
  const displayData = isMobile && subjectPerformance.length > 6 
    ? subjectPerformance.slice(0, 6) 
    : subjectPerformance;

  return (
    <Card className={`${isMobile ? "p-3" : "p-6"} bg-gradient-to-br from-indigo-50 to-white border border-slate-100 shadow-sm`}>
      <h2 className={`${isMobile ? "text-base" : "text-xl"} font-semibold mb-2 sm:mb-4 flex items-center gap-2 bg-clip-text text-transparent bg-gradient-to-r from-purple-700 to-indigo-500`}>
        <Target className={`${isMobile ? "w-4 h-4" : "w-5 h-5"} text-purple-500`} />
        Strongest & Weakest Areas
      </h2>
      
      <ResponsiveChartContainer
        config={{
          margin: isMobile 
            ? { top: 5, right: 5, bottom: 5, left: 5 } 
            : { top: 5, right: 30, left: 50, bottom: 5 }
        }}
        mobileHeight={220}
        desktopHeight={300}
        mobilePadding="px-0"
        desktopPadding="px-0"
      >
        <BarChart
          data={displayData}
          layout="vertical"
        >
          <defs>
            <linearGradient id="strengthGradient" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#4ADE80" stopOpacity={0.8} />
              <stop offset="100%" stopColor="#22C55E" stopOpacity={1} />
            </linearGradient>
            <linearGradient id="weaknessGradient" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#FB923C" stopOpacity={0.8} />
              <stop offset="100%" stopColor="#F97316" stopOpacity={1} />
            </linearGradient>
            <filter id="bar-shadow" height="130%">
              <feDropShadow dx="0" dy="3" stdDeviation="2" floodColor="#6366F1" floodOpacity="0.2"/>
            </filter>
          </defs>
          <CartesianGrid strokeDasharray="3 3" opacity={0.2} horizontal={false} />
          <XAxis 
            type="number" 
            domain={[0, 100]} 
            tick={{ fill: '#6B7280', fontSize: isMobile ? (screenSize === 'xs' ? 8 : 10) : 12 }}
            axisLine={{ stroke: '#E5E7EB' }}
            tickFormatter={(value) => `${value}%`}
          />
          <YAxis 
            dataKey="subject" 
            type="category" 
            width={isMobile ? (screenSize === 'xs' ? 65 : 80) : 120}
            tick={{ fill: '#4B5563', fontSize: isMobile ? (screenSize === 'xs' ? 8 : 10) : 12 }}
            axisLine={{ stroke: '#E5E7EB' }}
            tickFormatter={(value) => isMobile && value.length > 12 ? `${value.substring(0, 10)}...` : value}
          />
          <ResponsiveChartTooltip
            formatter={(value: number) => [`${value.toFixed(1)}%`, 'Average Score']}
            contentStyle={{ 
              backgroundColor: 'white', 
              borderRadius: '8px', 
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              border: '1px solid #E5E7EB'
            }}
          />
          <Bar
            dataKey="averageScore"
            radius={[0, 4, 4, 0]}
            filter="url(#bar-shadow)"
          >
            {displayData.map((entry, index) => (
              <Cell 
                key={`cell-${index}`} 
                fill={entry.category === 'strength' ? 'url(#strengthGradient)' : 'url(#weaknessGradient)'} 
              />
            ))}
            <LabelList 
              dataKey="averageScore" 
              position="right"
              formatter={(value: number) => `${isMobile ? Math.round(value) : value.toFixed(1)}%`}
              style={{ 
                fill: '#4B5563', 
                fontWeight: 'bold', 
                fontSize: isMobile ? (screenSize === 'xs' ? 8 : 10) : 12
              }}
            />
          </Bar>
        </BarChart>
      </ResponsiveChartContainer>
      
      <div className="mt-2 sm:mt-4 flex justify-center gap-3 sm:gap-8">
        <div className="flex items-center gap-1 sm:gap-2">
          <div className="w-3 h-1.5 sm:w-4 sm:h-2 rounded-sm bg-gradient-to-r from-green-500 to-green-600"></div>
          <span className={`${isMobile ? "text-xs" : "text-sm"} text-gray-600 font-medium`}>≥75%</span>
        </div>
        <div className="flex items-center gap-1 sm:gap-2">
          <div className="w-3 h-1.5 sm:w-4 sm:h-2 rounded-sm bg-gradient-to-r from-orange-500 to-orange-600"></div>
          <span className={`${isMobile ? "text-xs" : "text-sm"} text-gray-600 font-medium`}>&lt;75%</span>
        </div>
      </div>
      
      {isMobile && subjectPerformance.length > 6 && (
        <div className="mt-1 text-xs text-center text-purple-600 font-medium">
          Showing top 6 of {subjectPerformance.length} subjects
        </div>
      )}
    </Card>
  );
};
